/* eslint-disable react/require-default-props */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import UserContext from '../../../hooks/context/UserContext';
import useAvatar from '../../../hooks/useAvatar';
import AppLayout from '../../../layouts/AppLayout';
import ApiService from '../../../services/api.service';
import TokenService from '../../../services/token.service';
import EnglishLevelsEnum from '../../Admin/Users/enums/englishLevels.enum';
import AvatarEditModal from './components/AvatarEditModal';
import CareerTimeline from './components/CareerTimeline';
import ProfileCard from './components/ProfileCard';

function Progress({ title }) {
  const { user, setUser } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [levels, setLevels] = useState([]);
  const [userLevel, setUserLevel] = useState(null);
  const [englishLevel, setEnglishLevel] = useState(null);
  const [departmentName, setDepartmentName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaveDisabled, setSaveDisabled] = useState(true);

  const userAvatar = useMemo(() => user?.avatar, [user?.avatar]);
  const gender = user?.gender || 'male';
  const { values, generateAvatar } = useAvatar({ initialValues: userAvatar, gender });

  useEffect(() => {
    async function fetchData() {
      let actualUser = null;

      try {
        actualUser = await ApiService.developer.getMySelf();
        const [hydraEnglish] = await Promise.all([
          ApiService.developer.getHydraEnglishLevel(user.email),
        ]);

        const hydraEnglishLevel = EnglishLevelsEnum[hydraEnglish.speaking];

        if (hydraEnglishLevel.value !== actualUser.englishLevel) {
          await ApiService.developer.updateUser(user._id, {
            englishLevel: hydraEnglishLevel.value,
          });
        }
      } catch {
        if (actualUser && !actualUser.englishLevel) {
          await ApiService.developer.updateUser(user._id, {
            englishLevel: EnglishLevelsEnum[0].value,
          });
        }
      }

      const [departmentLevels, departments] = await Promise.all([
        ApiService.developer.getDepartmentLevels(user.departmentId),
        ApiService.developer.getDepartments(),
      ]);

      actualUser = await ApiService.developer.getMySelf();

      const department = departments.find((el) => el._id === user.departmentId);

      setDepartmentName(department?.name || '');
      setEnglishLevel(actualUser.englishLevel);
      setUserLevel(actualUser.level);
      setLevels(departmentLevels);
      setIsLoading(false);
    }

    fetchData();
  }, [user.departmentId, user._id, user.email]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleSaveAvatar = async () => {
    try {
      const updatedUser = { ...user, avatar: values };
      await ApiService.developer.updateUser(user._id, updatedUser);
      const guestToken = await ApiService.developer.refreshToken(updatedUser._id);

      TokenService.removeAccessToken();
      TokenService.removeGuestToken();
      TokenService.setGuestToken(guestToken);

      setUser(updatedUser);
      setSaveDisabled(true);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating avatar:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const englishLevelDisplay = englishLevel
    ? Object.values(EnglishLevelsEnum).find((level) => level.value === englishLevel)?.display
    : '';

  useEffect(() => {
    if (userAvatar !== values) {
      setSaveDisabled(false);
    }
  }, [userAvatar, values]);

  // Calculate current level index for timeline
  const currentLevelIndex = useMemo(() => {
    if (!levels.length || !userLevel) return -1;
    return levels.findIndex((level) => level._id === userLevel);
  }, [levels, userLevel]);

  if (isLoading) {
    return (
      <AppLayout title={title}>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={title}>
      <div className="h-full overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Section - Left Column */}
            <div className="lg:col-span-1">
              <ProfileCard
                user={user}
                userAvatar={userAvatar}
                englishLevel={englishLevel}
                englishLevelDisplay={englishLevelDisplay}
                departmentName={departmentName}
                currentLevel={levels.find((level) => level._id === userLevel)}
                onEditAvatar={handleOpenModal}
              />
            </div>

            {/* Career Timeline - Right Column */}
            <div className="lg:col-span-2">
              <CareerTimeline levels={levels} currentLevelIndex={currentLevelIndex} />
            </div>
          </div>
        </div>
      </div>

      {/* Avatar Edit Modal */}
      <AvatarEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveAvatar}
        isSaveDisabled={isSaveDisabled}
        userAvatar={values}
        generateAvatar={generateAvatar}
        departmentName={departmentName}
        gender={gender}
      />
    </AppLayout>
  );
}

Progress.propTypes = {
  title: PropTypes.string.isRequired,
};

export default Progress;
